<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import MessageTemplatesHeader from './MessageTemplatesHeader.vue';
import PipelinesAPI from 'dashboard/api/pipelines';
import Modal from '../../../../components/Modal.vue';
import MessageTemplateForm from './MessageTemplateForm.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { emitter } from 'shared/helpers/mitt';

const emit = defineEmits(['switch-view']);
const { t } = useI18n();

const loading = ref(false);
const pipelines = ref([]);
const error = ref(null);
const selectedPipelineId = ref(null);
const isComponentMounted = ref(true);
const selectedCategory = ref('all');
const searchQuery = ref('');

// Estado para controlar o modal de confirmação
const showDeleteModal = ref(false);
const templateToDelete = ref(null);
const stageToDeleteFrom = ref(null);

// Estado para controlar o modal de edição
const showEditModal = ref(false);
const templateToEdit = ref(null);

// Estado para controlar o modal de criação
const showCreateModal = ref(false);
const newTemplateData = ref(null);

// Estado para controlar o modal de preview
const showPreviewModal = ref(false);
const templateToPreview = ref(null);

// Categorias disponíveis
const categories = [
  {
    id: 'all',
    label: t('PIPELINE.MESSAGE_TEMPLATES.CATEGORIES.ALL'),
    icon: 'globe',
  },
  {
    id: 'follow-up',
    label: t('PIPELINE.MESSAGE_TEMPLATES.CATEGORIES.FOLLOW_UP'),
    icon: 'mail',
  },
  {
    id: 'proposal',
    label: t('PIPELINE.MESSAGE_TEMPLATES.CATEGORIES.PROPOSAL'),
    icon: 'document',
  },
  {
    id: 'negotiation',
    label: t('PIPELINE.MESSAGE_TEMPLATES.CATEGORIES.NEGOTIATION'),
    icon: 'people',
  },
  {
    id: 'closing',
    label: t('PIPELINE.MESSAGE_TEMPLATES.CATEGORIES.CLOSING'),
    icon: 'checkmark-circle',
  },
  {
    id: 'support',
    label: t('PIPELINE.MESSAGE_TEMPLATES.CATEGORIES.SUPPORT'),
    icon: 'chat-help',
  },
  {
    id: 'other',
    label: t('PIPELINE.MESSAGE_TEMPLATES.CATEGORIES.OTHER'),
    icon: 'more-horizontal',
  },
];

// Busca os funis e seus templates
const fetchPipelines = async () => {
  try {
    loading.value = true;
    if (!isComponentMounted.value) return;
    const response = await PipelinesAPI.get();
    pipelines.value = response.data;

    // Seleciona o primeiro funil por padrão
    if (pipelines.value.length && !selectedPipelineId.value) {
      selectedPipelineId.value = pipelines.value[0].id;
    }
  } catch (err) {
    if (!isComponentMounted.value) return;
    error.value = err.message;
  } finally {
    if (!isComponentMounted.value) return;
    loading.value = false;
  }
};

// Retorna o funil selecionado e suas etapas organizadas
const selectedPipeline = computed(() => {
  const pipeline = pipelines.value.find(f => f.id === selectedPipelineId.value);
  if (!pipeline) return null;

  return {
    ...pipeline,
    stages: Object.entries(pipeline.stages || {})
      .map(([id, stage]) => ({
        id,
        ...stage,
        message_templates: (stage.message_templates || []).map(template => ({
          ...template,
          category: template.category || 'other',
          variables: extractVariables(template.content),
        })),
      }))
      .sort((a, b) => a.position - b.position),
  };
});

// Extrai variáveis do template
const extractVariables = content => {
  const matches = content.match(/\{([^}]+)\}/g);
  return matches ? matches.map(m => m.slice(1, -1)) : [];
};

// Templates filtrados por categoria e busca
const filteredTemplates = computed(() => {
  if (!selectedPipeline.value) return [];

  const allTemplates = selectedPipeline.value.stages.flatMap(stage =>
    stage.message_templates.map(template => ({
      ...template,
      stageName: stage.name,
      stageId: stage.id,
      stageColor: stage.color,
    }))
  );

  let filtered = allTemplates;

  // Filtrar por categoria
  if (selectedCategory.value !== 'all') {
    filtered = filtered.filter(t => t.category === selectedCategory.value);
  }

  // Filtrar por busca
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(
      t =>
        t.title.toLowerCase().includes(query) ||
        t.content.toLowerCase().includes(query)
    );
  }

  return filtered;
});

// Contador de templates por categoria
const categoryCount = computed(() => {
  if (!selectedPipeline.value) return {};

  const allTemplates = selectedPipeline.value.stages.flatMap(
    stage => stage.message_templates
  );

  const counts = { all: allTemplates.length };

  categories.slice(1).forEach(cat => {
    counts[cat.id] = allTemplates.filter(t => t.category === cat.id).length;
  });

  return counts;
});

const handleTemplateCreated = async () => {
  try {
    loading.value = true;
    if (!isComponentMounted.value) return;
    await fetchPipelines();
  } catch (err) {
    if (!isComponentMounted.value) return;
    error.value = err.message;
  } finally {
    if (!isComponentMounted.value) return;
    loading.value = false;
  }
};

// Função para truncar texto
const truncateText = (text, length) => {
  if (!text) return '';
  return text.length > length ? `${text.slice(0, length)}...` : text;
};

// Função para abrir o modal de confirmação
const confirmDelete = (template, stageId) => {
  templateToDelete.value = template;
  stageToDeleteFrom.value = stageId;
  showDeleteModal.value = true;
};

// Função para deletar template após confirmação
const handleDeleteTemplate = async () => {
  try {
    loading.value = true;
    const currentPipeline = pipelines.value.find(
      f => f.id === selectedPipelineId.value
    );

    if (!currentPipeline) {
      throw new Error(t('PIPELINE.ERRORS.PIPELINE_NOT_FOUND'));
    }

    const stageId = stageToDeleteFrom.value;
    const templateId = templateToDelete.value.id;

    // Verificar se o stage existe
    if (!currentPipeline.stages || !currentPipeline.stages[stageId]) {
      throw new Error(t('PIPELINE.MESSAGES.STAGE_NOT_FOUND'));
    }

    // Deep clone do pipeline para não modificar o original
    const updatedStages = JSON.parse(JSON.stringify(currentPipeline.stages));

    // Verificar se message_templates existe e encontrar o template
    if (!updatedStages[stageId].message_templates) {
      updatedStages[stageId].message_templates = [];
    }

    const templateIndex = updatedStages[stageId].message_templates.findIndex(
      t => t.id === templateId
    );

    if (templateIndex > -1) {
      // Remover o template
      updatedStages[stageId].message_templates.splice(templateIndex, 1);

      // Atualizar o pipeline com os stages modificados
      await PipelinesAPI.update(currentPipeline.id, {
        stages: updatedStages,
      });

      // Recarregar os pipelines
      await fetchPipelines();

      // Mostrar mensagem de sucesso
      emitter.emit('newToastMessage', {
        message: t('PIPELINE.MESSAGE_TEMPLATES.DELETE_SUCCESS'),
        action: { type: 'success' },
      });
    }

    // Fecha o modal após deletar
    showDeleteModal.value = false;
    templateToDelete.value = null;
    stageToDeleteFrom.value = null;
  } catch (err) {
    error.value = err.message;
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGE_TEMPLATES.DELETE_ERROR'),
      action: { type: 'error' },
    });
  } finally {
    loading.value = false;
  }
};

// Função para editar template
const handleEditTemplate = (template, stageId) => {
  templateToEdit.value = {
    ...template,
    pipeline_id: selectedPipelineId.value,
    stage_id: stageId,
    isEditing: true,
  };
  showEditModal.value = true;
};

// Handler para quando o template é salvo
const handleTemplateSaved = template => {
  showEditModal.value = false;
  handleTemplateCreated(template);
};

// Função para abrir o modal de criação
const openCreateModal = (pipelineId, stageId) => {
  newTemplateData.value = {
    pipeline_id: pipelineId,
    stage_id: stageId,
  };
  showCreateModal.value = true;
};

// Handler para quando o template é criado
const handleNewTemplateCreated = template => {
  showCreateModal.value = false;
  handleTemplateCreated(template);
};

// Função para abrir preview
const openPreview = template => {
  templateToPreview.value = template;
  showPreviewModal.value = true;
};

// Função para duplicar template
const handleDuplicateTemplate = async (template, stageId) => {
  try {
    loading.value = true;
    const currentPipeline = pipelines.value.find(
      f => f.id === selectedPipelineId.value
    );

    if (!currentPipeline) {
      throw new Error(t('PIPELINE.PIPELINE_NOT_FOUND'));
    }

    const duplicated = {
      ...template,
      id: Date.now(),
      title: `${template.title} (copy)`,
      created_at: new Date().toISOString(),
    };

    const updatedPipeline = { ...currentPipeline };
    updatedPipeline.stages[stageId].message_templates.push(duplicated);

    await PipelinesAPI.update(currentPipeline.id, updatedPipeline);
    await fetchPipelines();
  } catch (err) {
    error.value = err.message;
  } finally {
    loading.value = false;
  }
};

// Função para obter ícone da categoria
const getCategoryIcon = category => {
  const cat = categories.find(c => c.id === category);
  return cat?.icon || 'document';
};

// Função para obter cor da categoria
const getCategoryColor = category => {
  const colors = {
    'follow-up': 'text-woot-500',
    proposal: 'text-violet-500',
    negotiation: 'text-yellow-500',
    closing: 'text-green-500',
    support: 'text-red-500',
    other: 'text-slate-500',
  };
  return colors[category] || 'text-slate-500';
};

// Função para obter cor de fundo da categoria
const getCategoryBgColor = category => {
  const colors = {
    'follow-up': 'bg-woot-100 dark:bg-woot-900/20',
    proposal: 'bg-violet-100 dark:bg-violet-900/20',
    negotiation: 'bg-yellow-100 dark:bg-yellow-900/20',
    closing: 'bg-green-100 dark:bg-green-900/20',
    support: 'bg-red-100 dark:bg-red-900/20',
    other: 'bg-slate-100 dark:bg-slate-800',
  };
  return colors[category] || 'bg-slate-100 dark:bg-slate-800';
};

onMounted(() => {
  isComponentMounted.value = true;
  fetchPipelines();
});

onBeforeUnmount(() => {
  isComponentMounted.value = false;
  selectedPipelineId.value = null;
  pipelines.value = [];
  error.value = null;
  loading.value = false;
});
</script>

<template>
  <div class="flex flex-col h-full w-full bg-slate-50 dark:bg-slate-900">
    <MessageTemplatesHeader
      @switch-view="view => emit('switch-view', view)"
      @template-created="handleTemplateCreated"
    />

    <div class="flex-1 p-6 overflow-auto">
      <!-- Loading State -->
      <div v-if="loading" class="flex items-center justify-center h-full">
        <span
          class="w-8 h-8 border-2 border-slate-200 border-t-woot-500 rounded-full animate-spin"
        />
      </div>

      <!-- Error State -->
      <div
        v-else-if="error"
        class="flex flex-col items-center justify-center h-full text-center p-4"
      >
        <fluent-icon icon="error" size="24" class="text-red-500 mb-2" />
        <p class="text-slate-600 dark:text-slate-400">
          {{ t('PIPELINE.MESSAGE_TEMPLATES.ERROR') }}
        </p>
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          class="mt-2"
          @click="fetchPipelines"
        >
          {{ t('RETRY') }}
        </Button>
      </div>

      <!-- Empty State -->
      <div
        v-else-if="!pipelines.length"
        class="flex flex-col items-center justify-center h-full text-center p-4"
      >
        <div
          class="w-16 h-16 rounded-full bg-gradient-to-br from-woot-100 to-violet-100 dark:from-woot-900/20 dark:to-violet-900/20 flex items-center justify-center mb-4"
        >
          <fluent-icon icon="document" size="32" class="text-woot-500" />
        </div>
        <h3 class="text-lg font-medium mb-1">
          {{ t('PIPELINE.MESSAGE_TEMPLATES.EMPTY') }}
        </h3>
        <p class="text-sm text-slate-600 dark:text-slate-400 max-w-md">
          {{ t('PIPELINE.MESSAGE_TEMPLATES.EMPTY_DESCRIPTION') }}
        </p>
      </div>

      <!-- Templates Library -->
      <div v-else class="h-full flex flex-col">
        <!-- Pipeline Selector & Search -->
        <div
          class="flex items-center gap-4 mb-6 max-md:flex-col max-md:items-stretch"
        >
          <div
            class="flex gap-2 overflow-x-auto flex-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <button
              v-for="pipeline in pipelines"
              :key="pipeline.id"
              class="px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all"
              :class="
                pipeline.id === selectedPipelineId
                  ? 'bg-woot-500 text-white shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
              "
              @click="selectedPipelineId = pipeline.id"
            >
              {{ pipeline.name }}
            </button>
          </div>

          <div
            class="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 w-64 max-md:w-full"
          >
            <fluent-icon icon="search" size="16" class="text-slate-400" />
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="t('PIPELINE.MESSAGE_TEMPLATES.SEARCH_PLACEHOLDER')"
              class="flex-1 bg-transparent border-none outline-none text-sm"
            />
          </div>
        </div>

        <!-- Category Filter -->
        <div class="flex gap-2 mb-6 overflow-x-auto pb-2">
          <button
            v-for="category in categories"
            :key="category.id"
            class="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all border-2"
            :class="
              selectedCategory === category.id
                ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/20 text-woot-600 dark:text-woot-400'
                : 'border-transparent text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
            "
            @click="selectedCategory = category.id"
          >
            <fluent-icon :icon="category.icon" size="16" />
            <span>{{ category.label }}</span>
            <span
              v-if="categoryCount[category.id]"
              class="bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs px-2 py-0.5 rounded-full"
            >
              {{ categoryCount[category.id] }}
            </span>
          </button>
        </div>

        <!-- Templates Grid -->
        <div
          v-if="filteredTemplates.length"
          class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
        >
          <div
            v-for="template in filteredTemplates"
            :key="`${template.stageId}-${template.id}`"
            class="border-2 border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden transition-all duration-200 hover:border-woot-300 dark:hover:border-woot-600 hover:shadow-lg group"
          >
            <!-- Card Header -->
            <div
              class="flex items-center justify-between gap-2 px-4 py-3 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700"
            >
              <div class="flex items-center gap-2 flex-1 min-w-0">
                <div
                  class="w-3 h-3 rounded-full flex-shrink-0"
                  :style="{ backgroundColor: template.stageColor }"
                />
                <span
                  class="text-xs font-medium text-slate-600 dark:text-slate-400 truncate"
                >
                  {{ template.stageName }}
                </span>
              </div>
              <div class="flex items-center gap-1">
                <button
                  class="p-1.5 rounded-lg text-slate-500 hover:bg-white dark:hover:bg-slate-700 transition-colors opacity-0 group-hover:opacity-100"
                  :title="t('PIPELINE.MESSAGE_TEMPLATES.PREVIEW')"
                  @click="openPreview(template)"
                >
                  <fluent-icon icon="eye-show" size="16" />
                </button>
                <button
                  class="p-1.5 rounded-lg text-slate-500 hover:bg-white dark:hover:bg-slate-700 transition-colors opacity-0 group-hover:opacity-100"
                  :title="t('PIPELINE.MESSAGE_TEMPLATES.DUPLICATE')"
                  @click="handleDuplicateTemplate(template, template.stageId)"
                >
                  <fluent-icon icon="copy" size="16" />
                </button>
                <button
                  class="p-1.5 rounded-lg text-slate-500 hover:bg-white dark:hover:bg-slate-700 transition-colors opacity-0 group-hover:opacity-100"
                  :title="t('PIPELINE.MESSAGE_TEMPLATES.EDIT')"
                  @click="handleEditTemplate(template, template.stageId)"
                >
                  <fluent-icon icon="edit" size="16" />
                </button>
                <button
                  class="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors opacity-0 group-hover:opacity-100"
                  :title="t('PIPELINE.MESSAGE_TEMPLATES.DELETE')"
                  @click="confirmDelete(template, template.stageId)"
                >
                  <fluent-icon icon="delete" size="16" />
                </button>
              </div>
            </div>

            <!-- Card Body -->
            <div class="p-4">
              <div class="flex items-start gap-3 mb-3">
                <div
                  class="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  :class="getCategoryBgColor(template.category)"
                >
                  <fluent-icon
                    :icon="getCategoryIcon(template.category)"
                    size="20"
                    :class="getCategoryColor(template.category)"
                  />
                </div>
                <div class="flex-1 min-w-0">
                  <h3
                    class="text-base font-semibold text-slate-800 dark:text-slate-100 mb-1 line-clamp-1"
                  >
                    {{ template.title }}
                  </h3>
                  <p
                    class="text-sm text-slate-600 dark:text-slate-400 line-clamp-2"
                  >
                    {{ template.content }}
                  </p>
                </div>
              </div>

              <!-- Variables -->
              <div
                v-if="template.variables.length"
                class="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800"
              >
                <span
                  class="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2 block"
                >
                  {{ t('PIPELINE.MESSAGE_TEMPLATES.VARIABLES') }}:
                </span>
                <div class="flex flex-wrap gap-1.5">
                  <span
                    v-for="variable in template.variables.slice(0, 3)"
                    :key="variable"
                    class="text-xs px-2 py-1 bg-violet-100 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 rounded-md font-mono"
                  >
                    {{ variable }}
                  </span>
                  <span
                    v-if="template.variables.length > 3"
                    class="text-xs px-2 py-1 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-md font-mono"
                  >
                    +{{ template.variables.length - 3 }}
                  </span>
                </div>
              </div>

              <!-- Features -->
              <div class="flex gap-2 mt-3">
                <span
                  v-if="template.webhook?.enabled"
                  class="flex items-center gap-1 text-xs px-2 py-1 bg-woot-100 dark:bg-woot-900/20 text-woot-600 dark:text-woot-400 rounded-md"
                  :title="t('PIPELINE.MESSAGE_TEMPLATES.WEBHOOK_ENABLED')"
                >
                  <fluent-icon icon="globe" size="12" />
                  {{ t('PIPELINE.MESSAGE_TEMPLATES.WEBHOOK') }}
                </span>
                <span
                  v-if="template.conditions?.enabled"
                  class="flex items-center gap-1 text-xs px-2 py-1 bg-woot-100 dark:bg-woot-900/20 text-woot-600 dark:text-woot-400 rounded-md"
                  :title="t('PIPELINE.MESSAGE_TEMPLATES.CONDITIONS_ENABLED')"
                >
                  <fluent-icon icon="filter" size="12" />
                  {{ t('PIPELINE.MESSAGE_TEMPLATES.CONDITIONS') }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty Search State -->
        <div
          v-else
          class="flex flex-col items-center justify-center h-full text-center py-12"
        >
          <fluent-icon icon="search" size="48" class="text-slate-300 mb-4" />
          <h3 class="text-lg font-medium mb-1">
            {{ t('PIPELINE.MESSAGE_TEMPLATES.NO_RESULTS') }}
          </h3>
          <p class="text-sm text-slate-600 dark:text-slate-400">
            {{ t('PIPELINE.MESSAGE_TEMPLATES.NO_RESULTS_DESCRIPTION') }}
          </p>
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            class="mt-4"
            @click="
              searchQuery = '';
              selectedCategory = 'all';
            "
          >
            {{ t('PIPELINE.MESSAGE_TEMPLATES.CLEAR_FILTERS') }}
          </Button>
        </div>
      </div>
    </div>

    <!-- Modal de Preview -->
    <Modal
      v-model:show="showPreviewModal"
      size="medium"
      :on-close="() => (showPreviewModal = false)"
    >
      <div class="p-6">
        <div
          class="flex items-center justify-between mb-4 pb-4 border-b border-slate-200 dark:border-slate-700"
        >
          <h3 class="text-xl font-semibold">{{ templateToPreview?.title }}</h3>
          <button
            class="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            @click="showPreviewModal = false"
          >
            <fluent-icon icon="dismiss" size="20" />
          </button>
        </div>

        <div class="space-y-4">
          <!-- Metadata -->
          <div
            class="grid grid-cols-3 max-md:grid-cols-1 gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg"
          >
            <div class="flex flex-col gap-1">
              <span
                class="text-xs font-medium text-slate-500 dark:text-slate-400"
              >
                {{ t('PIPELINE.MESSAGE_TEMPLATES.STAGE') }}:
              </span>
              <span class="text-sm text-slate-800 dark:text-slate-200">
                {{ templateToPreview?.stageName }}
              </span>
            </div>
            <div class="flex flex-col gap-1">
              <span
                class="text-xs font-medium text-slate-500 dark:text-slate-400"
              >
                {{ t('PIPELINE.MESSAGE_TEMPLATES.CATEGORY') }}:
              </span>
              <span class="text-sm text-slate-800 dark:text-slate-200">
                {{
                  categories.find(c => c.id === templateToPreview?.category)
                    ?.label
                }}
              </span>
            </div>
            <div class="flex flex-col gap-1">
              <span
                class="text-xs font-medium text-slate-500 dark:text-slate-400"
              >
                {{ t('PIPELINE.MESSAGE_TEMPLATES.CREATED') }}:
              </span>
              <span class="text-sm text-slate-800 dark:text-slate-200">
                {{
                  new Date(templateToPreview?.created_at).toLocaleDateString()
                }}
              </span>
            </div>
          </div>

          <!-- Content -->
          <div
            class="p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg min-h-[200px]"
          >
            <p class="whitespace-pre-wrap">{{ templateToPreview?.content }}</p>
          </div>

          <!-- Variables -->
          <div
            v-if="templateToPreview?.variables.length"
            class="p-4 bg-violet-50 dark:bg-violet-900/10 rounded-lg"
          >
            <h4 class="text-sm font-medium mb-2">
              {{ t('PIPELINE.MESSAGE_TEMPLATES.VARIABLES') }}:
            </h4>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="variable in templateToPreview?.variables"
                :key="variable"
                class="text-xs px-2 py-1 bg-violet-100 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 rounded-md font-mono"
              >
                {{ variable }}
              </span>
            </div>
          </div>
        </div>

        <div
          class="flex justify-end gap-2 mt-6 pt-4 border-t border-slate-200 dark:border-slate-700"
        >
          <Button
            variant="ghost"
            color="slate"
            @click="showPreviewModal = false"
          >
            {{ t('PIPELINE.ACTIONS.CLOSE') }}
          </Button>
          <Button
            variant="solid"
            color="blue"
            @click="
              handleEditTemplate(templateToPreview, templateToPreview.stageId);
              showPreviewModal = false;
            "
          >
            <template #icon>
              <fluent-icon icon="edit" size="16" />
            </template>
            {{ t('PIPELINE.MESSAGE_TEMPLATES.EDIT') }}
          </Button>
        </div>
      </div>
    </Modal>

    <!-- Modal de Confirmação -->
    <Modal
      v-model:show="showDeleteModal"
      size="tiny"
      :on-close="() => (showDeleteModal = false)"
    >
      <div class="p-6">
        <h3
          class="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2"
        >
          {{ t('PIPELINE.MESSAGE_TEMPLATES.DELETE') }}
        </h3>
        <p class="text-sm text-slate-600 dark:text-slate-400 mb-6">
          {{
            t('PIPELINE.MESSAGE_TEMPLATES.DELETE_CONFIRM_MESSAGE', {
              name: templateToDelete?.title || '',
            })
          }}
        </p>
        <div class="flex justify-end gap-2">
          <Button
            variant="ghost"
            color="slate"
            @click="showDeleteModal = false"
          >
            {{ t('PIPELINE.ACTIONS.CANCEL') }}
          </Button>
          <Button
            variant="solid"
            color="ruby"
            :disabled="loading"
            @click="handleDeleteTemplate"
          >
            <template #icon>
              <fluent-icon icon="delete" size="16" />
            </template>
            {{ t('PIPELINE.MESSAGE_TEMPLATES.DELETE') }}
          </Button>
        </div>
      </div>
    </Modal>

    <!-- Modal de Edição -->
    <Modal
      v-model:show="showEditModal"
      size="large"
      :on-close="() => (showEditModal = false)"
    >
      <div class="p-6">
        <MessageTemplateForm
          :initial-data="templateToEdit"
          :is-editing="true"
          @saved="handleTemplateSaved"
          @close="showEditModal = false"
        />
      </div>
    </Modal>

    <!-- Modal de Criação -->
    <Modal
      v-model:show="showCreateModal"
      size="large"
      :on-close="() => (showCreateModal = false)"
    >
      <div class="p-6">
        <MessageTemplateForm
          :initial-data="newTemplateData"
          :is-editing="false"
          @saved="handleNewTemplateCreated"
          @close="showCreateModal = false"
        />
      </div>
    </Modal>
  </div>
</template>

<!-- CSS migrated to Tailwind classes -->
